import React from 'react';
import PropTypes from 'prop-types';
import clone from 'clone';

import {connectModuleEnv} from '../../oe-module-env';
import {frefFromRef, frefToRef} from '../../../lib/react/higher-order-components';
import {oeInterfaceManager} from '../../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../../react-oe/oe-interface-adapter';
import OEIcon from '../../elements/oe-icon';
import OEButton from '../../elements/controls/oe-button';
import OEToolbox from '../../../lib/tools/oe-toolbox';
import OEIconCodes from '../../../lib/oe-icon-codes';
import OEResizeObserver from '../../../lib/components/oe-resize-observer';
import retardUpdate from '../../../lib/react/update-retarder';
import {OEXRayIcarusCustomPropKey, OEXRayIcarusMode, OEXRayIcarusId, OEXRayIcarusModel, OEXRayIcarusOverlayNotifications} from './oe-xray-icarus-model';
import OEOverlayTextBox from '../../overlays/elements/oe-overlay-text-box';
import OEXRayIcarusTextBox from './oe-xray-icarus-text-box';
import OEXRayIcarusSubItemView from './oe-xray-icarus-sub-item-view';

export class OEXRayIcarusOverlay extends React.PureComponent {

    constructor(props) {
        super(props);

        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);

        this.model = OEXRayIcarusModel.shared();
        if(!this.model.data) this.model.setData(OEXRayIcarusModel.stdData());

        this.edgeOffsets = {top: 0, right: 0, bottom: 0, left: 0};

        this.visible = false;
        this.mode = OEXRayIcarusMode.uninitialized;

        this.xRayContainerSize = null;

        this.textBox = {
            available: false,
            isOpen: false,
            layoutType: OEOverlayTextBox.ExtLayoutType.right
        };

        this.state = {
            visible: this.visible,
            mode: this.mode,
            xRayImage: null,
            xRayContainerSize: this.xRayContainerSize,
            textBox: clone(this.textBox),
            strings: {
                noXRayImage: ''
            }
        };

        this.updateLanguage = this.updateLanguage.bind(this);
        this.onCustomPropertyChanged = this.onCustomPropertyChanged.bind(this);

        this.onWindowResized = this.onWindowResized.bind(this);
        this.onXRayContainerResize = this.onXRayContainerResize.bind(this);

        this.onResetCameraBtnPressed = this.onResetCameraBtnPressed.bind(this);
        this.onTextBoxBtnPressed = this.onTextBoxBtnPressed.bind(this);
        this.onTextBoxToggle = this.onTextBoxToggle.bind(this);
        this.onTextBoxToLeftRightBtnPressed = this.onTextBoxToLeftRightBtnPressed.bind(this);
    }

    componentDidMount()    {
        window.addEventListener('resize', this.onWindowResized);
    }

    componentWillUnmount()  {
        this.props.uiStateManager.updateState({contextMenu: {areaRestriction: { right: {$set: 0}}}});
        window.removeEventListener('resize', this.onWindowResized);
    }

    componentWillReceiveProps(nextProps) {
        if(!OEToolbox.shallowEqual(this.props.ui, nextProps.ui, true))  {
            this.updateVPFrame(nextProps);
            this.updateContextMenuContainerRestriction(nextProps);
        }
    }

    setStateUpdate(spec)   {
        OEToolbox.updateComponentState(this, spec);
    }

    getEdgeOffsets()    {
        return this.edgeOffsets;
    }

    setTextBoxOpen(open)    {
        if((!this.textBox.available && open) || this.textBox.isOpen == open)    return;
        this.textBox.isOpen = open;
        this.setStateUpdate({textBox: {isOpen: {$set: open}}});
    }

    setTextBoxLayoutType(layoutType)    {
        if(this.textBox.layoutType == layoutType)    return;
        this.textBox.layoutType = layoutType;
        this.setStateUpdate({textBox: {layoutType: {$set: layoutType}}});
    }

    onConnect()  {
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.languageChanged, this.updateLanguage);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.customPropertyChanged, this.onCustomPropertyChanged);

        // for debugging pupose -> start immediately in x-ray modus (with text box)
        /*
        this.oe.sharedInterface.getUIControllerMediaCenter().setActualMediaItemByCategoryIndex(8);
        this.oe.sharedInterface.setCustomIntProperty(OEXRayIcarusCustomPropKey.mode, OEXRayIcarusMode.xRay);
        this.setTextBoxOpen(true);
        */
    }

    onRelease()  {
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.languageChanged, this.updateLanguage);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.customPropertyChanged, this.onCustomPropertyChanged);
    }

    updateLanguage()   {
        let si = this.oe.sharedInterface;
        this.setState({strings: {
            noXRayImage: si.getLocalizedStringEnc('no_xray_image_text')
        }});
    }

    updateVPFrame(props) {
        if(!this.oe.isReady() || !this.xRayContainerSize || !window.innerWidth) return;
        props = props || this.props;
        let wSize = {w: window.innerWidth, h: window.innerHeight};
        let frame = {x: props.ui.mainMenuWidth, y: props.ui.bottomEdgeOffset};
        frame.z = wSize.w - this.xRayContainerSize.w - frame.x - props.ui.rightEdgeOffset;
        frame.w = wSize.h - props.ui.capBarTopEdgeOffset - frame.y;
        frame = {x: 2 * frame.x / wSize.w - 1, y: 2 * frame.y / wSize.h - 1, z: 2 * frame.z / wSize.w, w: 2 * frame.w / wSize.h};
        this.oe.sharedInterface.setCustomFloatProperty(OEXRayIcarusCustomPropKey.vpFrame, {vec: frame});
    }

    updateContextMenuContainerRestriction(props) {
        props = props || this.props;
        let right = this.xRayContainerSize && this.visible && this.mode === OEXRayIcarusMode.xRay ? this.xRayContainerSize.w + props.ui.rightEdgeOffset : 0;
        this.props.uiStateManager.updateState({contextMenu: {areaRestriction: { right: {$set: right}}}});
    }

    updateEdgeOffsets()    {
        let edgeOffsets = {top: 0, right: 0, bottom: 0, left: 0};
        if(OEToolbox.shallowEqual(this.edgeOffsets, edgeOffsets)) return;
        this.edgeOffsets = edgeOffsets;
        if(this.props.onEdgeOffsetsChanged) this.props.onEdgeOffsetsChanged(this, this.edgeOffsets, true);
    }

    update()   {
        if(!this.oe.isReady())  return;
        const si = this.oe.sharedInterface;

        let mode = si.getCustomProperty(OEXRayIcarusCustomPropKey.mode).value;
        let id = si.getCustomProperty(OEXRayIcarusCustomPropKey.itemId);
        id = new OEXRayIcarusId(id.value);
        let item = OEXRayIcarusModel.shared().getProperItem(id);
        let resolvedItem = OEXRayIcarusModel.shared().getResolvedItem(id);

        this.visible = mode != OEXRayIcarusMode.uninitialized;
        this.mode = mode;

        retardUpdate(this, () => {
            this.setState({visible: this.visible, mode: this.mode});

            if((item && item.xRayImage) || !this.model.isIdleOrNull(id))   {
                let xRayImage = item && item.xRayImage ? item.xRayImage : null;
                this.setState({xRayImage: xRayImage ? 'images/xRay/xRay/' + xRayImage : null});
            }

            //
            let textSrc = resolvedItem ? (mode == OEXRayIcarusMode.std ? resolvedItem.textSrc : (mode == OEXRayIcarusMode.xRay ? resolvedItem.xRayTextSrc : undefined)) : undefined;
            let textAvailable = !!textSrc;

            if(this.textBox.available !== textAvailable) {
                this.textBox.available = textAvailable;
                if(!textAvailable)  this.textBox.isOpen = false;
                this.setState({textBox: clone(this.textBox)});
            }
        });

        //
        this.updateContextMenuContainerRestriction();
    }

    updateState(released)   {
        if(!this.oe.isReady() || released === true)   {
            this.visible = false;
            this.textBox = {
                available: false,
                isOpen: false,
                layoutType: OEOverlayTextBox.ExtLayoutType.right
            };
            this.setState({
                visible: false,
                textBox: clone(this.textBox)
            });
            this.updateEdgeOffsets();
            this.updateContextMenuContainerRestriction();
            return;
        }

        this.updateVPFrame();

        retardUpdate(this, () => {
            this.updateLanguage();
            this.update();
        });
    }

    onCustomPropertyChanged(message, userInfo)   {
        if(userInfo.key === OEXRayIcarusCustomPropKey.mode || userInfo.key === OEXRayIcarusCustomPropKey.itemId)   {
            this.update();
        }
    }

    onWindowResized()   {
        this.updateVPFrame();
    }

    onXRayContainerResize(sender, size) {
        if(OEToolbox.shallowEqual(this.xRayContainerSize, size))    return;
        this.xRayContainerSize = size;
        this.setState({xRayContainerSize: size});
        this.updateVPFrame();
        this.updateContextMenuContainerRestriction();
    }

    renderResetCameraBtn(visible = true)  {
        return (
            <OEButton
                className="transparent-btn view-port-label-text-color reset-camera center"
                style={{visibility: visible ? 'visible' : 'hidden', opacity: visible ? '1' : '0'}}
                onPressed={this.onResetCameraBtnPressed}
            >
                <OEIcon code={OEIconCodes.xRayIcarus.resetCamera}/>
            </OEButton>
        );
    }

    renderTextBoxBtn(visible = true, className = '')  {
        visible = visible && this.state.visible && this.state.textBox.available;
        return (
            <OEButton
                className={'transparent-btn view-port-label-text-color text-box ' + className + ' screenshot-filter-show'}
                style={{visibility: visible ? 'visible' : 'hidden', opacity: visible ? '1' : '0'}}
                onPressed={this.onTextBoxBtnPressed}
            >
                <OEIcon code={OEIconCodes.xRayIcarus.textBox}/>
            </OEButton>
        );
    }

    renderTextBox(visibility = true) {
        return (
            <OEXRayIcarusTextBox
                moduleId={this.props.moduleId}
                isHidden={!visibility}
                isOpen={this.state.textBox.isOpen}
                extLayoutType={this.state.textBox.layoutType}
                onToggle={this.onTextBoxToggle}
                onToLeftRightBtnPressed={this.onTextBoxToLeftRightBtnPressed}
            />
        );
    }

    renderSubItemView() {
        let style;
        if(this.state.mode == OEXRayIcarusMode.xRay && this.state.xRayContainerSize) {
            style = {right: this.state.xRayContainerSize.w};
        }

        return (
            <OEXRayIcarusSubItemView
                moduleId={this.props.moduleId}
                style={style}
            />
        );
    }

    renderXRayContainer()  {
        const visible = this.state.visible && this.state.mode == OEXRayIcarusMode.xRay;

        return (
            <div
                className="screenshot-filter-show"
                style={{visibility: visible ? 'visible' : 'hidden', opacity: visible ? '1' : '0'}}
            >
                <div
                    className="x-ray-container-backdrop"
                    style={this.state.xRayContainerSize ? {width: this.state.xRayContainerSize.w} : undefined}
                />

                <div className="x-ray-container">
                    <OEResizeObserver onResize={this.onXRayContainerResize} />

                    {/*this.renderResetCameraBtn(visible)*/}
                    {/*this.renderTextBoxBtn(visible, 'center')*/}
                    {/*this.renderTextBox(this.state.textBox.layoutType === OEOverlayTextBox.ExtLayoutType.right)*/}

                    <img
                        style={this.state.xRayContainerSize ? {maxHeight: this.state.xRayContainerSize.h} : undefined}
                        src={this.state.xRayImage ? this.state.xRayImage : 'images/xRay/xRay/empty.png'}
                    />

                    {this.state.xRayImage ? undefined :
                        <div className="no-x-ray">
                            <span>{this.state.strings.noXRayImage}</span>
                        </div>
                    }
                    
                </div>
            </div>
        );
    }

    renderTextBoxContainer() {
        const visible = this.state.visible;
        const width = this.state.mode == OEXRayIcarusMode.xRay && this.state.xRayContainerSize ? this.state.xRayContainerSize.w : undefined;

        return (
            <div
                className={'text-box-container layout-type-' + this.state.textBox.layoutType + ' screenshot-filter-show'}
                style={{visibility: visible ? 'visible' : 'hidden', opacity: visible ? '1' : '0', width: width}}
            >
                {this.renderTextBox(this.state.mode != OEXRayIcarusMode.xRay || this.state.textBox.layoutType === OEOverlayTextBox.ExtLayoutType.left)}
            </div>
        );
    }

    render() {
        const modeClassName = OEXRayIcarusMode.modeToString(this.state.mode);

        return (
            <React.Fragment>
                <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>
                <div className={'overlay xray-icarus '+ modeClassName + ' std-label-text-color'}>

                    {this.renderXRayContainer()}

                    {/*this.renderTextBoxContainer()*/}

                    {/*this.renderTextBoxBtn(this.state.mode != OEXRayIcarusMode.xRay)*/}

                    {this.renderSubItemView()}

                </div>
            </React.Fragment>
        );
    }

    onResetCameraBtnPressed()   {
        this.oe.sharedInterface.postNotification(OEXRayIcarusOverlayNotifications.cameraResetBtnPressed, null);
    }

    onTextBoxBtnPressed()   {
        this.setTextBoxOpen(!this.textBox.isOpen);
    }

    onTextBoxToggle()   {
        this.setTextBoxOpen(false);
    }

    onTextBoxToLeftRightBtnPressed(layoutType)    {
        this.setTextBoxLayoutType(layoutType);
    }
}

OEXRayIcarusOverlay.defaultProps = {
    moduleId: ''
};

OEXRayIcarusOverlay.propTypes = {
    moduleId: PropTypes.string
};

export default frefFromRef(connectModuleEnv((env) => {
    return {
        uiStateManager: env.component.uiStateManager,
        ui: {
            mainMenuWidth: env.ui.mainMenu.width,
            capBarTopEdgeOffset: env.ui.capBar.edgeOffsets.top,
            rightEdgeOffset: env.ui.edgeOffsets.right,
            bottomEdgeOffset: env.ui.bottomEdgeOffset.value
        }
    };
})(frefToRef(OEXRayIcarusOverlay)));
